/**
 * The lesson flow's availability source.
 *
 * The one place the Lesson Time step asks "when is this instructor free?" and
 * "which bay is free for the time I chose?". Both read
 * `GET /lessons/club/:clubId/availability`, the lesson flow's own endpoint: the
 * two shared availability endpoints resolve only the standard and demo flows,
 * and the shared bay endpoint applies a subscription allow-list a lesson does
 * not declare.
 *
 * An instructor's OWN weekly schedule governs a lesson, not the club's public
 * hours — a lesson can legitimately be offered before the club opens or after
 * it closes. Nothing here may filter a slot or a bay against club hours.
 *
 * Both hooks hand back the standard flow's own element shapes, so
 * `TimeSlotSelector` and `BaySelector` render a lesson without knowing it is
 * one. Same exported names as the mobile app's source.
 *
 * A lesson is one length at a time (the member picks 60/90/120), so one request
 * answers both questions: the slots ARE lessons of that length, and each names
 * its bays.
 *
 * @module pages/lessons/lessonAvailabilitySource
 */

import { useMemo } from 'react';
import type { Bay } from '@lgc/database/types';
import type { BaySelectorBayItem } from '@butter/ui/domains/booking';
import { LESSON_GRID_INTERVAL_MINUTES, lessonBaysForSlot, lessonRangeMinutes } from '@butter/library/lessons';
import { timeStringToMinutes } from '@butter/library/datetime';
import { useLessonAvailability } from '~/services/internalApi/lessons';
import type { ApiLessonAvailabilitySlot } from '~/services/internalApi/lessons';
import type { TimeSlot } from '~/pages/booking/components/TimeSlotSelector';

// Module scope — referential stability (docs/conventions/react-state.md). These
// sit in query keys, render conditions and dependency arrays.
const NO_SLOTS: TimeSlot[] = [];
const NO_BAYS: BaySelectorBayItem[] = [];

const FAILED_MESSAGE = "We couldn't load this instructor's availability. Please try again.";

export interface LessonSlotsResult {
  /** One entry per bookable start, in the standard selector's shape. Never undefined. */
  slots: TimeSlot[];
  /** No answer yet, and one is on its way. */
  isLoading: boolean;
  /** Set when there is nothing to show and nothing coming without help. */
  error: string | null;
  retry: () => void;
  /** The one reason the whole day is empty, in the server's words. Null when there is none. */
  emptyReason: string | null;
  /** `YYYY-MM-DD`, club-local — the furthest day this club takes bookings for. */
  lastBookableDate: string | null;
}

export interface LessonBaysResult {
  /** Bays free for the chosen time that this instructor may teach in. Never undefined. */
  bays: BaySelectorBayItem[];
  isLoading: boolean;
  error: string | null;
  retry: () => void;
}

/** A lesson slot → the standard selector's slot. A lesson has no member-only or prime-time hours. */
export function toSelectorSlot(slot: ApiLessonAvailabilitySlot): TimeSlot {
  return {
    startMinutes: slot.startMinutes,
    durationMinutes: slot.durationMinutes,
    displayTime: slot.displayTime,
    isAvailable: slot.isAvailable,
    startTime: slot.startTime,
    endTime: slot.endTime,
    isPrimeTime: false,
    // The selector only needs "bookable or not"; the sentence carries the reason.
    availabilityState: slot.isAvailable ? 'PUBLIC' : 'BLOCKED',
    canBookPublic: slot.isAvailable,
    canBookMember: slot.isAvailable,
    isPremiumPricing: false,
    stateDescription: slot.unavailableReason ?? (slot.isAvailable ? 'Available' : 'Not available'),
    canStart: slot.canStart,
    startBayIds: slot.startBayIds,
  };
}

/**
 * `club.bays` rows → the bay cards' shape. The club read types its bays as
 * database rows, whose timestamps are `Date`s; on the wire they are strings,
 * and the cards read neither.
 */
function toSelectorBay(bay: Bay): BaySelectorBayItem {
  return {
    id: bay.id,
    clubId: bay.clubId,
    name: bay.name,
    capacity: bay.capacity,
    description: bay.description,
    image: bay.image,
    premiumCents: bay.premiumCents,
    features: bay.features,
    color: bay.color,
    createdAt: String(bay.createdAt),
    updatedAt: String(bay.updatedAt),
    isAvailable: true,
  };
}

/** A given instructor's bookable lesson starts on a date. */
export function useLessonSlots(params: {
  clubId: number | undefined;
  instructorId: number | null;
  date: string | null;
  /** The lesson length the grid offers. @unit minutes */
  durationMinutes: number;
}): LessonSlotsResult {
  const { durationMinutes } = params;
  const durations = useMemo(() => [durationMinutes], [durationMinutes]);
  const query = useLessonAvailability({
    clubId: params.clubId,
    instructorId: params.instructorId,
    date: params.date,
    durations,
    slotInterval: LESSON_GRID_INTERVAL_MINUTES,
  });
  const { availability, hasAnswer, failure, isLoading, isError, isPaused, refetch } = query;

  const slots = useMemo(
    () => (availability ? availability.slots.map(toSelectorSlot) : NO_SLOTS),
    [availability],
  );

  // A refusal envelope, a thrown request, or an offline pause with nothing on
  // screen. A failed background refetch is none of these: the last grid stays.
  const failed = !!failure || (!hasAnswer && (isError || isPaused));

  return {
    slots,
    isLoading: isLoading && !hasAnswer,
    error: failed ? (failure?.message ?? FAILED_MESSAGE) : null,
    retry: refetch,
    emptyReason: availability?.unavailableReason?.message ?? null,
    lastBookableDate: availability?.horizon.lastBookableDate ?? null,
  };
}

/**
 * Bays free for an instructor's already-chosen time. Asks for the exact length
 * chosen — the same request the slots made, so this is a cache hit.
 */
export function useLessonBays(params: {
  clubId: number | undefined;
  instructorId: number | null;
  date: string | null;
  startTime: string | null;
  endTime: string | null;
  /** `club.bays` — the full records. The availability answer names bays by id and name only. */
  clubBays: Bay[] | undefined;
}): LessonBaysResult {
  const { clubId, instructorId, date, startTime, endTime, clubBays } = params;
  const chosen = startTime !== null && endTime !== null;
  const durationMinutes = chosen ? lessonRangeMinutes(startTime, endTime) : 0;
  const startMinutes = startTime !== null ? timeStringToMinutes(startTime) : null;

  const durations = useMemo(() => [durationMinutes], [durationMinutes]);
  const query = useLessonAvailability({
    clubId,
    instructorId,
    date,
    durations,
    slotInterval: LESSON_GRID_INTERVAL_MINUTES,
    enabled: durationMinutes > 0,
  });

  const { availability } = query;
  const bays = useMemo(() => {
    if (!availability || startMinutes === null || !clubBays) return NO_BAYS;
    const free = new Set(lessonBaysForSlot(availability, startMinutes, durationMinutes).map((bay) => bay.id));
    if (free.size === 0) return NO_BAYS;
    return clubBays.filter((bay) => free.has(bay.id)).map(toSelectorBay);
  }, [availability, clubBays, startMinutes, durationMinutes]);

  const failed = !!query.failure || (!query.hasAnswer && (query.isError || query.isPaused));

  return {
    bays,
    isLoading: durationMinutes > 0 && query.isLoading && !query.hasAnswer,
    error: failed ? (query.failure?.message ?? FAILED_MESSAGE) : null,
    retry: query.refetch,
  };
}
